import type { Middleware } from "@reduxjs/toolkit";
import { isFulfilled, isRejectedWithValue } from "@reduxjs/toolkit";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { HttpStatusCode } from "axios";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import { attachRtkQueryError, readRtkQueryError } from "_shared/_errors/api-error";
import { reportApiError } from "_shared/_errors/client-error-reporter";
import { SCHEMA_FAILURE_ERROR } from "_shared/_errors/schema-failure-error";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  clearSystemNotificationBanner,
  setSystemNotificationBanner,
} from "_shared/_providers/banner/system-notification-banner-provider";
import { AlertType } from "_shared/_views/system-notification-banner/system-notification-banner";
import Session from "users/__session/session";
import { currentReturnRoute } from "users/auth/return-routes/login-return-route";
import { isSignOutInProgress } from "users/__session/sign-out-in-progress";

export type AttorneyHubApiMeta = FetchBaseQueryMeta & { endpointName: string };

const rawBaseQuery = fetchBaseQuery({
  baseUrl: AttorneyHubAPIService.apiBaseUrl,
  // Resolve fetch when a request is made. Jest's node environment does not
  // expose fetch during module initialization, even though API specs install
  // it before dispatching requests.
  fetchFn: (...args) => globalThis.fetch(...args),
  prepareHeaders: (headers, { arg }) => {
    const authToken = Session.loadFromStorage(() => {}).authToken?.value;
    if (authToken) {
      headers.set("Authorization", `Bearer ${authToken}`);
    }

    // At this point `headers` holds only what the endpoint's own query() declared,
    // and fetchBaseQuery has not yet applied its application/json default. So an
    // absent Content-Type means the endpoint expressed no preference: PATCH bodies
    // are RFC 6902 patch documents unless an endpoint names its own media type.
    if (isPatchArg(arg) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json-patch+json");
    }
    return headers;
  },
});

function isPatchArg(arg: string | FetchArgs): boolean {
  return typeof arg !== "string" && arg.method?.toUpperCase() === "PATCH";
}

function isGetArg(arg: string | FetchArgs): boolean {
  return typeof arg === "string" || (arg.method ?? "GET").toUpperCase() === "GET";
}

/**
 * How long to wait before each repeat of a read the API answered 503.
 *
 * The API answers 503 when the database did not answer in time — a connection it could not open,
 * a command that timed out (#6182). That is over in a second or two, so a read is sent again twice
 * before its failure reaches the page. Only a GET is repeated: a write the server may already have
 * applied is the caller's to retry.
 */
export const UNAVAILABLE_READ_RETRY_DELAYS_MS = [500, 1500] as const;

function waitUnlessAborted(milliseconds: number, signal: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve(true);
    }, milliseconds);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

const rawBaseQueryRetryingUnavailableReads: typeof rawBaseQuery = async (args, queryApi, extraOptions) => {
  let result = await rawBaseQuery(args, queryApi, extraOptions);
  if (!isGetArg(args)) return result;

  for (const delay of UNAVAILABLE_READ_RETRY_DELAYS_MS) {
    if (result.error?.status !== HttpStatusCode.ServiceUnavailable) break;
    if (!(await waitUnlessAborted(delay, queryApi.signal))) break;
    result = await rawBaseQuery(args, queryApi, extraOptions);
  }
  return result;
};

/**
 * Answers a 401 by ending the session and going to the login screen, carrying the route to come
 * back to.
 *
 * Nothing read a 401 before this. Each of the ~560 places that turn a failed query into a snackbar
 * showed its own message, so a session the server had stopped accepting looked like a page full of
 * unrelated failures — "Unable to load entity members", "Failed to load conversations" — over an
 * authenticated shell, with no way back to a login form (issue #5661). The app's own expiry timer
 * only covers a token that lapses by this browser's clock; a token the server rejects for any other
 * reason reaches nothing but this.
 *
 * It does nothing when no credential was held, because then the 401 says what the app already knew
 * and a redirect would only take the person off a public page. That also makes it idempotent:
 * `logout` clears the stored token, so the next 401 in the same burst finds nothing to end.
 */
function endSessionOnUnauthorized(error: FetchBaseQueryError): void {
  if (error.status !== HttpStatusCode.Unauthorized) return;

  const session = Session.loadFromStorage(() => {});
  if (!session.authToken) return;

  void session.logout(currentReturnRoute());
}

const baseQueryWithEndpointName: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  unknown,
  AttorneyHubApiMeta
> = async (args, queryApi, extraOptions) => {
  const result = await rawBaseQueryRetryingUnavailableReads(args, queryApi, extraOptions as Record<string, never>);
  if (result.error !== undefined) {
    endSessionOnUnauthorized(result.error);
    const error = readRtkQueryError(result.error, { headers: result.meta?.response?.headers });
    // A request that fails once sign-out has begun is one the sign-out orphaned — usually a refetch
    // sent after the token was cleared. It says nothing about the API, so it is not reported (#5827).
    if (!isSignOutInProgress()) reportApiError(error, queryApi.endpoint);
    return {
      error: attachRtkQueryError(result.error, error),
      meta: result.meta ? { ...result.meta, endpointName: queryApi.endpoint } : undefined,
    };
  }

  return {
    ...result,
    meta: result.meta ? { ...result.meta, endpointName: queryApi.endpoint } : undefined,
  };
};

export function getApiEndpointName(meta: unknown): string | undefined {
  if (!meta || typeof meta !== "object" || !("endpointName" in meta)) {
    return undefined;
  }

  const endpointName = (meta as { endpointName: unknown }).endpointName;
  return typeof endpointName === "string" ? endpointName : undefined;
}

/**
 * Every cache tag the one shared api instance knows about.
 *
 * Exported because a test that preloads api state has to name all of them, and hand-listing
 * them there meant that adding a tag for one domain broke an unrelated spec in another. Build
 * that fixture from this list instead.
 */
export const API_TAG_TYPES = [
  "AccessRequest",
  "AdministratorInvitation",
  "AssistantConversation",
  "AssistantFeedback",
  "Bookmark",
  "CalendarEvent",
  "ClientProposalTemplate",
  "Company",
  "Document",
  "EntityMember",
  "EntityOfficerInvitation",
  "FeeSchedule",
  "FeeScheduleCategory",
  "FeeScheduleTemplate",
  "Individual",
  "KnowledgeBaseArticle",
  "KnowledgeBaseCategory",
  "KnowledgeBaseLibrary",
  "NetworkedGroup",
  "NetworkConnectionNote",
  "NetworkInvitation",
  "Proposal",
  "ProposalTemplate",
  "Team",
  "TeamTemplate",
  "UserCoworker",
  "UserInformation",
  "UserEntityMemberInvitation",
  "UserNetworkConnection",
  "VendorRolloutBoard",
  "Marketplace",
  "Forum",
  "ForumInvitation",
  "ForumSubscriber",
  "Message",
  "MessageAttachment",
  "Notification",
  "SavedSearch",
  "SystemNotification",
  "Permission",
  "PermissionTemplate",
  "WorkAgreement",
  "OnboardingTemplateCatalog",
  "OnboardingTemplateDetail",
  "TutorialPreferences",
  "NotificationDeliveryPreferences",
  "NotificationEmailReminderOperations",
] as const;

export type ApiTagType = (typeof API_TAG_TYPES)[number];

export const api = createApi({
  baseQuery: baseQueryWithEndpointName,
  tagTypes: API_TAG_TYPES,
  onSchemaFailure: logSchemaFailure,
  catchSchemaFailure: () => SCHEMA_FAILURE_ERROR,

  endpoints: () => ({}),
});

const browserConnectionFailureStatus = "FETCH_ERROR";

function isApiAction(action: unknown): action is { type: string } {
  return (
    typeof action === "object" &&
    action !== null &&
    "type" in action &&
    typeof (action as { type: unknown }).type === "string" &&
    (action as { type: string }).type.startsWith(`${api.reducerPath}/`)
  );
}

function isBrowserConnectionFailure(payload: unknown): payload is { status: typeof browserConnectionFailureStatus } {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "status" in payload &&
    (payload as { status: unknown }).status === browserConnectionFailureStatus
  );
}

export const apiServerDownCheck: Middleware = () => (next) => (action) => {
  const result = next(action);
  if (isFulfilled(action)) {
    clearSystemNotificationBanner();
    return result;
  }
  if (!isApiAction(action) || !isRejectedWithValue(action)) {
    return result;
  }
  if (isAbortedError(action.error)) {
    return result;
  }

  if (isBrowserConnectionFailure(action.payload)) {
    setSystemNotificationBanner(
      AlertType.Error,
      "Attorney Hub is currently unavailable. Please check your internet connection and try again later.",
    );
  }

  return result;
};
function isAbortedError(error: unknown): error is { message: string } {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    (error as { message: string }).message.toLowerCase().includes("aborted")
  );
}
